/**
 * Hero video (`hero--video.twig`): attaches a `src` to the server-rendered
 * `<video>` only on viewports that should play it. Phones, reduced-motion
 * visitors and save-data connections keep the poster frame. HD is used from
 * 1024px up, SD below that, always muted; `preload="none"` keeps the file
 * request off the critical path until Alpine decides otherwise.
 */
import type { AlpineComponent } from '~/js/offer-generator/alpine'

export interface HeroVideoSources {
    hd: string
    sd: string
}

interface HeroVideoData {
    loaded: boolean
    playing: boolean
    muted: boolean
    load(autoplay: boolean): void
    play(): Promise<void>
    togglePlay(): void
    toggleMute(): void
}

interface HeroVideoConnection {
    saveData?: boolean
}

export default (sources: HeroVideoSources): AlpineComponent<HeroVideoData> => ({
    loaded: false,
    playing: false,
    muted: true,

    init() {
        const connection = (navigator as Navigator & { connection?: HeroVideoConnection }).connection

        if (
            window.matchMedia('(min-width: 768px)').matches &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
            !connection?.saveData
        ) {
            this.load(true)
        }
    },

    load(autoplay: boolean) {
        const video = this.$refs.video as HTMLVideoElement | undefined

        if (!video || this.loaded) {
            return
        }

        video.src = window.matchMedia('(min-width: 1024px)').matches ? sources.hd : sources.sd
        video.muted = true
        this.loaded = true

        if (autoplay) {
            this.play()
        }
    },

    async play() {
        const video = this.$refs.video as HTMLVideoElement | undefined

        if (!video) {
            return
        }

        try {
            await video.play()
            this.playing = true
        } catch {
            this.playing = false
        }
    },

    togglePlay() {
        const video = this.$refs.video as HTMLVideoElement | undefined

        if (!video) {
            return
        }

        if (this.playing) {
            video.pause()
            this.playing = false
            return
        }

        if (!this.loaded) {
            this.load(true)
            return
        }

        this.play()
    },

    toggleMute() {
        const video = this.$refs.video as HTMLVideoElement | undefined

        this.muted = !this.muted

        if (video) {
            video.muted = this.muted
        }
    },
})
